import { daDK } from '@mui/x-data-grid/locales';

import { Box, Chip, Typography } from '@mui/material';
import { DataGridPremium } from '@mui/x-data-grid-premium';
import { DateTime } from 'luxon';
import EnergyXplorerStatusCell from './EnergyXplorerStatusCell';
import EnergyXplorerToolbar from './EnergyXplorerToolbar';

import { useLocalStorageJson } from '@/hooks/useLocalStorageJson';

// * Icons
import CheckCircleRoundedIcon from '@mui/icons-material/CheckCircleRounded';
import ErrorRoundedIcon from '@mui/icons-material/ErrorRounded';
import HelpOutlineRoundedIcon from '@mui/icons-material/HelpOutlineRounded';
import PriorityHighRoundedIcon from '@mui/icons-material/PriorityHighRounded';
import WarningRoundedIcon from '@mui/icons-material/WarningRounded';

export default function EnergyXplorerDatagrid(props) {
  const {
    initialRows = [],
    isLoading = true,
    setSelectedRow = () => {},
  } = props;

  function getStatusIcon(status) {
    switch (status) {
      case 'Success':
        return { Color: 'success', StatusIcon: CheckCircleRoundedIcon };
      case 'Warning':
        return { Color: 'warning', StatusIcon: WarningRoundedIcon };
      case 'Error':
        return { Color: 'error', StatusIcon: ErrorRoundedIcon };
      case 'Critical':
        return { Color: 'error', StatusIcon: PriorityHighRoundedIcon };
      default:
        return { Color: 'default', StatusIcon: HelpOutlineRoundedIcon };
    }
  }

  const [dataGridState, setDataGridState] = useLocalStorageJson(
    `EnergyXplorer-Service-DataGrid-Presets`,
    {
      presets: [
        {
          name: 'Default',
          visibilityList: {},
          density: 'standard',
          sortModel: [],
          filterModel: { items: [], quickFilterValues: [] },
        },
      ],
      chosenPreset: 0,
    },
  );

  const updateChosenPreset = (patch) =>
    setDataGridState((prev) => ({
      ...prev,
      presets: prev.presets.map((preset, idx) =>
        idx === prev.chosenPreset ? { ...preset, ...patch } : preset,
      ),
    }));

  const handleVisibilityChange = (model) => {
    updateChosenPreset({ visibilityList: model });
  };

  const handleDensityChange = (newDensity) => {
    updateChosenPreset({ density: newDensity });
  };

  const handleSortModelChange = (newModel) => {
    updateChosenPreset({ sortModel: newModel });
  };

  const handleFilterModelChange = (newModel) => {
    updateChosenPreset({ filterModel: newModel });
  };

  const currentPreset = dataGridState.presets[dataGridState.chosenPreset];

  const initialState = {
    sorting: {
      sortModel: [
        {
          field: 'Name',
          sort: 'desc',
        },
      ],
    },
    // rowGrouping: {
    //   model: ['AccountId'],
    // },
  };

  const columns = [
    { field: 'AccountId' },
    { field: 'Account' },
    { field: 'name' },
    { field: 'energyGroupId' },
    { field: 'nodeCount' },
    { field: 'initiativesCount' },
    { field: 'hasService' },
    {
      field: 'ExecutionTime',
      renderCell: (params) => {
        if (!params.value) return '-';
        const dt = DateTime.fromISO(params.value);
        if (!dt.isValid) return '-';
        return dt.toLocaleString(DateTime.DATETIME_MED);
      },
    },
    {
      field: 'QoS',
      headerName: 'QoS Score',
      flex: 1,
      type: 'number',
      sortable: true,
      // ✅ grab the score directly from systemQoS
      // valueGetter: (params) => params.row?.systemQoS?.SystemQoSScore ?? null,
      renderCell: (params) => {
        const overall = params?.row?.systemQoS?.OverallStatus;
        const { StatusIcon, Color } = getStatusIcon(overall);

        if (params.value == null) return '-';

        return (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              // justifyContent: 'center',
              height: '100%',
              width: '100%',
            }}
          >
            <Chip
              variant="outlined"
              icon={
                <StatusIcon
                  sx={{
                    fontSize: 16,
                    color: (theme) =>
                      Color === 'default'
                        ? theme.palette.text.disabled
                        : theme.palette[Color].main,
                  }}
                />
              }
              label={overall}
              color={Color}
              size="small"
              sx={{ mr: 1, width: 100 }}
            />
            <Typography fontWeight="bold">{params.value.toFixed(1)}</Typography>
          </Box>
        );
      },
    },
    {
      field: 'Status',
      headerName: 'Status',
      flex: 1,
      sortable: false,
      filterable: false,
      renderCell: (params) => (
        <Box display="flex" alignItems="center" height="100%">
          <EnergyXplorerStatusCell qos={params.row?.systemQoS} />
        </Box>
      ),
    },
  ].map((c) => ({ flex: 1, ...c })); // keep your flex default

  return (
    <DataGridPremium
      localeText={daDK.components.MuiDataGrid.defaultProps.localeText}
      rows={initialRows}
      columns={columns}
      // initialState={initialState}
      loading={isLoading}
      defaultGroupingExpansionDepth={-1}
      columnVisibilityModel={currentPreset.visibilityList}
      onColumnVisibilityModelChange={handleVisibilityChange}
      onDensityChange={(density) => handleDensityChange(density)}
      density={currentPreset.density}
      sortModel={currentPreset.sortModel}
      onSortModelChange={handleSortModelChange}
      filterModel={currentPreset.filterModel}
      onFilterModelChange={handleFilterModelChange}
      slots={{
        toolbar: EnergyXplorerToolbar,
      }}
      slotProps={{
        loadingOverlay: {
          variant: 'linear-progress',
          noRowsVariant: 'skeleton',
        },
        toolbar: {
          dataGridState: dataGridState,
          setDataGridState: setDataGridState,
          filterModel: currentPreset.filterModel,
        },
      }}
      onRowClick={(params) => {
        setSelectedRow(params.row);
      }}
      sx={{
        '.MuiDataGrid-aggregationColumnHeaderLabel': {
          display: 'none',
        },
        '& .cell-left-border': {
          borderLeft: (theme) => `1px solid ${theme.palette.divider}`,
        },
        '& .left-margin-cell': {
          paddingLeft: 7,
        },
        '& .autogenerated': {
          fontWeight: 'bold',
        },
        '& .MuiDataGrid-columnHeaderTitle': {
          whiteSpace: 'break-spaces',
          lineHeight: 1.5,
          fontWeight: 'bold',
        },
        '& .MuiDataGrid-columnHeaderTitleContainerContent': {
          height: '100%',
        },
        '& .MuiDataGrid-aggregationColumnHeader': {
          height: '100%',
        },
        '&.MuiDataGrid-root .MuiDataGrid-columnHeader--alignRight .MuiDataGrid-columnHeaderTitleContainer':
          {
            pl: 1,
          },
        border: 'none',
        '& .MuiDataGrid-cell:focus, & .MuiDataGrid-cell:focus-within, & .MuiDataGrid-columnHeader:focus, & .MuiDataGrid-columnHeader:focus-within':
          {
            outline: 'none',
          },
        '& .MuiDatagrid-root': {
          border: 0,
        },
        '.MuiDataGrid-withBorderColor': {
          border: 'none !important',
        },
        '& .MuiDatagrid-footerContainer': {
          border: 0,
        },
        '& .MuiDatagrid-bottomContainer': {
          border: 0,
        },
        '& .MuiDataGrid-main': {
          backgroundColor: (theme) =>
            theme.palette.mode === 'light' ? '#fff' : '#121212',
          borderRadius: 2,
          border: (theme) => `1px solid ${theme.palette.divider}`,
        },
        '& .MuiDataGrid-columnHeaders': {
          borderBottom: '1px solid',
          borderColor: 'divider',
        },
        '& .MuiDataGrid-columnsContainer': {
          backgroundColor: 'blue',
        },
        '& .selected-row': {
          backgroundColor: (theme) => theme.palette.action.selected,
          '&:hover': {
            backgroundColor: (theme) => theme.palette.action.selected,
          },
        },
      }}
    />
  );
}
